/**
 * Artifacts: the records a turn produced or pointed at (a draft, an invoice, a contract review, a project),
 * shown in the conversation as chips that open the record in the workspace pane.
 *
 * Only what the creator should look at stays in the conversation (`needsReview`: drafts, invoices, contract
 * reviews, pitches). Everything else a turn changed (tasks, saved links and files, project updates…) is listed in
 * its `changes` record in the workspace pane, with an Undo per record and for the whole change (`changeRecords`).
 *
 * They're derived from typed data only, never from the agent's prose: the changeset's recorded writes
 * (`GET /changesets/:id?ops=true` → object_type, pk, after) and `ui.action` open/highlight targets.
 * Internal records (memory files, knowledge, scripts, check-ins) never become chips.
 */
import { routeFor } from './actions'

export type ArtifactType =
    | 'draft' | 'thread' | 'invoice' | 'agreement' | 'project' | 'deliverable' | 'company' | 'contact'
    | 'task' | 'outreach' | 'file' | 'browser' | 'changes'

export interface ArtifactRef {
    type: ArtifactType
    id: string
    title: string
    subtitle?: string
    /** What the turn did to it. */
    verb?: 'created' | 'updated' | 'opened' | 'deleted'
    /** The record as the changeset wrote it (a draft's subject/body/to/thread, an invoice's totals). */
    data?: Record<string, any>
    /** Several records of one kind, shown as a single chip ("12 tasks"). */
    items?: ArtifactRef[]
}

export interface ChangesetOp {
    seq: number
    object_type: string | null
    table: string
    op: string
    pk: unknown
    before: Record<string, any> | null
    after: Record<string, any> | null
}

export const ARTIFACT_KINDS: Record<ArtifactType, { label: string; plural: string; icon: string; tone: string }> = {
    draft: { label: 'Email draft', plural: 'drafts', icon: 'fa-pen-line', tone: 'orange' },
    thread: { label: 'Email thread', plural: 'threads', icon: 'fa-envelope', tone: 'blue' },
    invoice: { label: 'Invoice', plural: 'invoices', icon: 'fa-file-invoice-dollar', tone: 'emerald' },
    agreement: { label: 'Contract review', plural: 'contracts', icon: 'fa-file-signature', tone: 'violet' },
    project: { label: 'Project', plural: 'projects', icon: 'fa-folder', tone: 'amber' },
    deliverable: { label: 'Deliverable', plural: 'deliverables', icon: 'fa-clapperboard-play', tone: 'amber' },
    company: { label: 'Brand', plural: 'brands', icon: 'fa-building', tone: 'neutral' },
    contact: { label: 'Contact', plural: 'contacts', icon: 'fa-user', tone: 'neutral' },
    task: { label: 'Task', plural: 'tasks', icon: 'fa-circle-check', tone: 'neutral' },
    outreach: { label: 'Pitch', plural: 'pitches', icon: 'fa-paper-plane', tone: 'blue' },
    file: { label: 'File', plural: 'files', icon: 'fa-file', tone: 'neutral' },
    browser: { label: 'Browser', plural: 'browsers', icon: 'fa-window', tone: 'blue' },
    changes: { label: 'Changes', plural: 'changes', icon: 'fa-list-check', tone: 'neutral' },
}

/** Records the creator should read, edit or send: they stay in the conversation as cards. */
const REVIEW: ReadonlySet<ArtifactType> = new Set(['draft', 'invoice', 'agreement', 'outreach'])
export function needsReview(a: Pick<ArtifactRef, 'type'>): boolean {
    return REVIEW.has(a.type)
}

/** A changeset's records in the workspace pane: the changes list with its undos (`id` is the changeset id). */
export function changesArtifact(changesetId: string, count: number): ArtifactRef {
    return { type: 'changes', id: changesetId, title: `${count} change${count === 1 ? '' : 's'}`, verb: 'opened' }
}

/**
 * The conversation's live browser as a workspace tab (one per conversation; `id` is the session id).
 * `startTaskId`: the viewer starts this task once it has measured its space, so the browser is created in the
 * pane's shape.
 */
export function browserArtifact(sessionId: string, title = 'Browser', startTaskId?: string): ArtifactRef {
    return { type: 'browser', id: sessionId, title, verb: 'opened', ...(startTaskId ? { data: { startTaskId } } : {}) }
}

/** Most interesting first: what the creator will want to open. */
const ORDER: ArtifactType[] = ['draft', 'invoice', 'agreement', 'outreach', 'thread', 'project', 'deliverable', 'company', 'contact', 'file', 'task', 'browser', 'changes']
/** More of one kind than this collapses into one chip. */
const GROUP_OVER = 2

const ALIASES: Record<string, ArtifactType> = { email_thread: 'thread', message: 'thread', contract: 'agreement', pitch: 'outreach' }

function asType(t: string | null | undefined): ArtifactType | null {
    if (!t) return null
    const k = (ALIASES[t] ?? t) as ArtifactType
    return k in ARTIFACT_KINDS ? k : null
}

function money(amount: unknown, currency: unknown): string | undefined {
    const n = Number(amount)
    if (!Number.isFinite(n) || n <= 0) return undefined
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: String(currency || 'USD'), maximumFractionDigits: 0 }).format(n)
    } catch {
        return `$${Math.round(n).toLocaleString()}`
    }
}

function recipients(to: unknown): string | undefined {
    const list = (Array.isArray(to) ? to : typeof to === 'string' ? to.split(',') : []).map((x) => String(x).trim()).filter(Boolean)
    if (!list.length) return undefined
    const name = (e: string) => (e.includes('<') ? e.split('<')[0].trim().replace(/"/g, '') : e)
    return `To ${name(list[0])}${list.length > 1 ? ` +${list.length - 1}` : ''}`
}

/** Title and subtitle for one record snapshot. */
export function describeRecord(type: ArtifactType, r: Record<string, any> | null | undefined): { title: string; subtitle?: string } {
    const row = r ?? {}
    const kind = ARTIFACT_KINDS[type]
    switch (type) {
        case 'draft':
            return { title: row.subject || 'Email draft', subtitle: recipients(row.to) }
        case 'thread':
            return { title: row.subject || row.title || 'Email thread' }
        case 'invoice': {
            const num = row.invoice_number ? `Invoice #${row.invoice_number}` : 'Invoice'
            const amount = money(row.total ?? row.amount_due ?? row.subtotal, row.currency)
            return { title: row.title && row.title !== 'Invoice' ? `${num} · ${row.title}` : num, subtitle: amount }
        }
        case 'agreement':
            return { title: row.name || 'Contract review' }
        case 'outreach':
            return { title: row.subject || row.title || row.name || 'Pitch' }
        default:
            return { title: row.title || row.name || row.label || row.email || kind.label }
    }
}

/** Openable records from a changeset's recorded writes, grouped and ordered for chips. */
export function artifactsFromOps(ops: ChangesetOp[] | null | undefined): ArtifactRef[] {
    const byKey = new Map<string, ArtifactRef>()
    for (const o of ops ?? []) {
        if (o.op === 'delete' || o.after?.deleted_at) continue
        let type = asType(o.object_type)
        let id = o.pk == null ? '' : String(typeof o.pk === 'object' ? (o.pk as any).id ?? '' : o.pk)
        let row = o.after ?? o.before ?? {}
        // An invoice line means its invoice changed.
        if (!type && o.object_type === 'invoice_line' && row.invoice) {
            type = 'invoice'
            id = String(row.invoice)
            row = {}
        }
        if (!type || !id) continue
        const key = `${type}:${id}`
        const prev = byKey.get(key)
        const merged = { ...(prev?.data ?? {}), ...row }
        const d = describeRecord(type, merged)
        byKey.set(key, {
            type, id, ...d, data: merged,
            verb: prev?.verb === 'created' || o.op === 'insert' ? 'created' : 'updated',
        })
    }
    // A draft is the thing to open, not the thread it replies to.
    const draftThreads = new Set([...byKey.values()].filter((a) => a.type === 'draft' && a.data?.thread).map((a) => String(a.data!.thread)))
    for (const [k, a] of byKey) if (a.type === 'thread' && draftThreads.has(a.id)) byKey.delete(k)

    const out: ArtifactRef[] = []
    for (const type of ORDER) {
        const list = [...byKey.values()].filter((a) => a.type === type)
        if (list.length > GROUP_OVER) {
            const kind = ARTIFACT_KINDS[type]
            const created = list.every((a) => a.verb === 'created')
            out.push({ type, id: `group:${type}`, title: `${list.length} ${kind.plural}`, subtitle: created ? 'Created' : 'Updated', items: list })
        } else {
            out.push(...list)
        }
    }
    return out
}

/** One record a change wrote, for the changes list: what it is, what happened to it, and its ops (by seq), which
 *  is what undoing just that record sends. */
export interface ChangeRecord {
    key: string
    /** The record as an artifact; `type` null for a kind with no viewer (a deal, a rate…), shown by its label. */
    ref: ArtifactRef | null
    label: string
    title: string
    verb: 'created' | 'updated' | 'deleted'
    seqs: number[]
}

/** Workspace internals that are never shown as changes (they still go back with an Undo all). */
const INTERNAL = /^(memory|knowledge|script|checkin|check_in|skill|agent_|workspace_|conversation)/

/** Every record a change wrote, one row each (no grouping), most interesting kinds first. */
export function changeRecords(ops: ChangesetOp[] | null | undefined): ChangeRecord[] {
    const byKey = new Map<string, ChangeRecord & { row: Record<string, any>; order: number }>()
    for (const o of ops ?? []) {
        const pk = o.pk == null ? '' : String(typeof o.pk === 'object' ? (o.pk as any).id ?? '' : o.pk)
        let type = asType(o.object_type)
        let id = pk
        let row = o.after ?? o.before ?? {}
        if (!type && o.object_type === 'invoice_line' && row.invoice) {
            type = 'invoice'
            id = String(row.invoice)
            row = {}
        }
        const raw = String(o.object_type || o.table || '')
        if (!type && (!raw || INTERNAL.test(raw))) continue
        const key = type ? `${type}:${id}` : `${o.table}:${pk}`
        const prev = byKey.get(key)
        const merged = { ...(prev?.row ?? {}), ...row }
        const verb: ChangeRecord['verb'] = prev?.verb === 'created' ? 'created' : o.op === 'insert' ? 'created' : o.op === 'delete' ? 'deleted' : 'updated'
        const label = type ? ARTIFACT_KINDS[type].label : raw.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
        const title = type ? describeRecord(type, merged).title : String(merged.title || merged.name || merged.label || label)
        const ref: ArtifactRef | null = type ? { type, id, ...describeRecord(type, merged), data: merged, verb } : null
        byKey.set(key, { key, ref, label, title, verb, seqs: [...(prev?.seqs ?? []), o.seq], row: merged,
                         order: type ? ORDER.indexOf(type) : ORDER.length })
    }
    return [...byKey.values()].sort((a, b) => a.order - b.order).map(({ row: _row, order: _order, ...r }) => r)
}

/** The record an `open` / `navigate` / `highlight` action points at, when it's one record. */
export function artifactFromTarget(t: Record<string, any> | null | undefined): ArtifactRef | null {
    const type = asType(t?.type)
    if (!t || !type || !t.id || Array.isArray(t.ids)) return null
    const d = describeRecord(type, t)
    return { type, id: String(t.id), title: t.label || t.title || d.title, subtitle: d.subtitle, verb: 'opened', data: { ...t } }
}

export function artifactKey(a: Pick<ArtifactRef, 'type' | 'id'>): string {
    return `${a.type}:${a.id}`
}

/** Where the record lives in the app ("Open in Lighthouse"). */
export function artifactRoute(a: ArtifactRef): string | null {
    if (a.items || a.type === 'browser' || a.type === 'changes') return null
    const d = a.data ?? {}
    if (a.type === 'draft') return routeFor({ type: 'draft', thread_id: d.thread ?? d.thread_id })
    if (a.type === 'deliverable') return routeFor({ type: 'deliverable', id: a.id, project_id: d.project ?? d.project_id })
    return routeFor({ type: a.type, id: a.id })
}

/**
 * Where an artifact opens:
 * - `pane`: the workspace pane beside the conversation (things you read, edit and send: drafts, threads,
 *   invoices, pitches, files, the live browser);
 * - `overlay`: over the whole conversation, closed back to the chat (contract reviews, projects, deliverables, tasks);
 * - `previewer`: the app's full-screen file previewer (files; a group of them opens as one swipeable set);
 * - `none`: shown as context only (brands and contacts open nothing).
 */
export type ArtifactSurface = 'pane' | 'overlay' | 'previewer' | 'none'

const SURFACES: Record<ArtifactType, ArtifactSurface> = {
    draft: 'pane', thread: 'pane', invoice: 'pane', outreach: 'pane', browser: 'pane', file: 'previewer',
    agreement: 'overlay', project: 'overlay', deliverable: 'overlay', task: 'overlay',
    company: 'none', contact: 'none', changes: 'pane',
}

export function artifactSurface(a: Pick<ArtifactRef, 'type' | 'items'>): ArtifactSurface {
    // A group is a list in the pane, unless nothing in it opens anywhere, or they're files (one previewer for all).
    if (a.items) return SURFACES[a.type] === 'none' || SURFACES[a.type] === 'previewer' ? SURFACES[a.type] : 'pane'
    return SURFACES[a.type]
}

/**
 * Artifacts that want more than the standard pane width by default (the invoice paper is a two-column page and
 * its editor adds a toolbar). The creator can still expand or shrink any pane from its header.
 */
export function artifactPrefersWide(a: Pick<ArtifactRef, 'type' | 'items'>): boolean {
    return a.type === 'invoice' && !a.items
}
